import React, { useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useSendAdminFormQuery } from '@entities/adminConversationUserHistory';
import { errorHandleMiddleware } from '@entities/attachment/lib';
import { TAttachmentDTOCamelCased, TAttachmentOnClient } from '@entities/attachment/model/types';
import { MassForm } from '@features/AdminConversationUsersSearchForm/ui/MassForm';
import { SingleForm } from '@features/AdminConversationUsersSearchForm/ui/SingleForm';
import { generateId } from '@shared/helpers';
import { useNuqsQueryState } from '@shared/hooks';
import { Checkbox, Flex, Select, UiButton, UiText } from '@shared/ui';

import cls from './AdminConversationUsersSearchForm.module.css';
import { useAdminConversationUsersSearchForm } from '../lib/hooks/useAdminConversationUsersSearchForm';

interface AdminConversationUsersSearchFormProps {
    classes?: string;
}

type TSelect = 'single' | 'mass';

interface IFormState {
    select: TSelect;
    userId: null | string;
    file: (TAttachmentOnClient | TAttachmentDTOCamelCased)[];
    addEmployee: boolean;
}

export const AdminConversationUsersSearchForm = ({
    classes,
}: AdminConversationUsersSearchFormProps) => {
    useAdminConversationUsersSearchForm();
    const { t } = useTranslation();
    const [form, setForm] = useState<IFormState>({
        select: 'single',
        userId: '',
        file: [],
        addEmployee: false,
    });

    const [readUserCount, setReadUserCount] = useState(null);

    const { mutateAsync } = useSendAdminFormQuery();
    const { state } = useNuqsQueryState('formId');
    const onSelect = (value: TSelect) => {
        setForm({ select: value, addEmployee: false, file: [], userId: '' });
        setReadUserCount(null);
    };

    const onUserId = (name: string, value: string) => {
        setForm((prevState) => ({ ...prevState, userId: value }));
    };

    const onChangeAddEmployee = () => {
        setForm((prevState) => ({ ...prevState, addEmployee: !prevState.addEmployee }));
    };

    const onAddFiles = async (files: File[]) => {
        const newAttachment: TAttachmentOnClient[] = files.map((file) => ({
            id: generateId(true) as number,
            file,
            isLoading: true,
        }));

        setForm((prevState) => ({ ...prevState, file: newAttachment }));
    };

    const uploadFilesHandler = errorHandleMiddleware({
        onAddFiles,
        t,
        maxAttachmentSizeInMb: 1000,
        maxOverallSizeInMb: 10000,
        isMultiple: false,
        validateExtensions: ['xlsx', 'xls', 'csv'],
    });

    const onSendForm = async () => {
        const fd = new FormData();

        if (form.select === 'single') {
            fd.append('user_id', form.userId);
            fd.append('type', 'user_id');
        } else {
            fd.append('type', 'spreadsheet');
            // @ts-ignore
            fd.append('spreadsheet', form.file[0].file);
            // @ts-ignore
            fd.append('add_employee', form.addEmployee ? 1 : 0);
        }

        const response = await mutateAsync({ formData: fd, id: +state });

        setReadUserCount(response.data?.user_added ? response.data?.user_added : null);
    };

    const searchFormMapping = {
        single: <SingleForm onUserId={onUserId} userIdValue={form.userId} />,
        mass: <MassForm uploadFilesHandler={uploadFilesHandler} file={form.file} />,
    };

    return (
        <div className={cn(cls.AdminConversationUsersSearchForm, [classes])}>
            <Flex fullWidth gap={12}>
                <Select
                    value={form.select}
                    selectRootClassName={cls.select}
                    classes={cls.select}
                    options={[
                        {
                            value: 'single',
                            label: 'Конкретному пользователю',
                        },
                        {
                            value: 'mass',
                            label: 'Массовая отправка',
                        },
                    ]}
                    onChange={onSelect}
                />
                <Checkbox
                    classes={cls.checkbox}
                    isDisabled={form.select === 'single'}
                    title="Разослать сотрудникам"
                    onClick={onChangeAddEmployee}
                    isActive={form.addEmployee}
                />
            </Flex>

            {searchFormMapping[form.select]}
            <Flex gap={12} justify="between" className={cls.buttonWrapper}>
                <a
                    className={cls.export}
                    // @ts-ignore
                    href={helpable.replaceUrlParameters(
                        // @ts-ignore
                        routable.getRoute('adminCommunicationItemUsersReportExport'),
                        {
                            _itemId_: state,
                        }
                    )}
                >
                    <UiButton btnColor="light" btnText="Экспорт в Excel" />
                </a>

                {readUserCount && form.select === 'mass' && (
                    <UiText variant="plain" classes={cls.userCount}>
                        Прочитано {readUserCount} пол.
                    </UiText>
                )}
                <UiButton
                    onClick={onSendForm}
                    btnText="Отправить"
                    disabled={
                        (form.select === 'single' && !form.userId) ||
                        (form.select === 'mass' && !form.file.length)
                    }
                />
            </Flex>
        </div>
    );
};
